'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getSocialLinks } from '@/app/components/shared/Footer/utils';
import { cn } from '@/app/utils';
import { AngleRight } from '../../icons/AngleRight';
import type { TestimonialsSectionData } from '../../types';
import { PortableTextContent } from '../PortableTextContent';
import { SectionShell } from '../section-shared';

export function TestimonialsSection({ section }: { section: TestimonialsSectionData }) {
  const reviews = section.reviews ?? [];
  const count = reviews.length;

  const TM_GAP = 32;
  const TM_ARROW_SIZE = 48;
  const TM_CARD_W = 640;

  const viewportRef = useRef<HTMLDivElement>(null);
  const [viewportW, setViewportW] = useState(0);
  const [current, setCurrent] = useState(() => count + Math.floor(count / 2));
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setViewportW(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const totalWidth = count * TM_CARD_W + Math.max(0, count - 1) * TM_GAP;
  const isCarousel = viewportW > 0 && totalWidth > viewportW;

  const slides = useMemo(() => (isCarousel ? [...reviews, ...reviews, ...reviews] : reviews), [reviews, isCarousel]);

  const slideW = TM_CARD_W + TM_GAP;
  const arrowOffset = TM_CARD_W / 2 + TM_ARROW_SIZE + TM_ARROW_SIZE / 2;

  const getOffset = useCallback(
    (idx: number): number => viewportW / 2 - TM_CARD_W / 2 - idx * slideW,
    [viewportW, slideW],
  );

  const handlePrev = useCallback(() => {
    if (animate) return;
    setAnimate(true);
    setCurrent((p) => p - 1);
  }, [animate]);

  const handleNext = useCallback(() => {
    if (animate) return;
    setAnimate(true);
    setCurrent((p) => p + 1);
  }, [animate]);

  const handleTransitionEnd = useCallback(() => {
    setAnimate(false);
    setCurrent((prev) => {
      if (prev < count) return prev + count;
      if (prev >= count * 2) return prev - count;
      return prev;
    });
  }, [count]);

  const socialLinks = getSocialLinks();

  return (
    <SectionShell>
      <div className="mb-10 w-full text-center sm:mb-8">
        <h2 className="font-semibold text-[36px] text-white leading-[1.2] sm:text-[28px]">{section.title}</h2>
      </div>

      <div
        ref={viewportRef}
        className="-translate-x-1/2 relative left-1/2 w-screen overflow-hidden sm:static sm:w-auto sm:translate-x-0 sm:overflow-visible sm:px-4"
      >
        <div className="group relative">
          {isCarousel && (
            <>
              <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-[168px] bg-gradient-to-r from-black to-transparent sm:hidden" />
              <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-[168px] bg-gradient-to-l from-black to-transparent sm:hidden" />
            </>
          )}

          <div
            className="flex sm:flex-col sm:gap-4 sm:[transform:none!important] sm:[transition:none!important]"
            style={{
              gap: `${TM_GAP}px`,
              visibility: viewportW > 0 ? 'visible' : 'hidden',
              ...(isCarousel
                ? {
                    transform: `translateX(${getOffset(current)}px)`,
                    transition: animate ? 'transform 500ms cubic-bezier(0.25, 1, 0.5, 1)' : 'none',
                  }
                : { justifyContent: 'center' }),
            }}
            onTransitionEnd={isCarousel ? handleTransitionEnd : undefined}
          >
            {slides?.map((review, i) => {
              const isDuplicate = isCarousel && (i < count || i >= count * 2);
              return (
                <div
                  key={isCarousel ? `${review._key}-${Math.floor(i / count)}` : review._key}
                  className={cn('sm:!w-full shrink-0', isDuplicate && 'sm:hidden')}
                  style={{ width: `${TM_CARD_W}px` }}
                >
                  <div className="flex min-h-[308px] flex-col gap-4 rounded-2xl bg-white/10 p-8 sm:min-h-0 sm:p-5">
                    <h3 className="font-semibold text-white text-xl leading-7">{review.title}</h3>
                    <div className="flex flex-1 flex-col justify-between">
                      <PortableTextContent value={review.content} />
                      <div className="mt-8">
                        <p className="line-clamp-1 font-medium text-white leading-6">{review.name}</p>
                        <p className="line-clamp-1 text-base text-white/55 leading-6">{review.role}</p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {isCarousel && (
            <>
              <button
                className="-translate-y-1/2 absolute top-1/2 z-20 flex size-12 items-center justify-center rounded-full bg-black/60 text-white backdrop-blur-[4px] transition-colors hover:bg-black/40 active:bg-black/80 sm:hidden"
                onClick={handlePrev}
                style={{ left: `calc(50% - ${arrowOffset}px)` }}
                type="button"
              >
                <AngleRight className="size-6 rotate-180" />
              </button>
              <button
                className="-translate-y-1/2 absolute top-1/2 z-20 flex size-12 items-center justify-center rounded-full bg-black/60 text-white backdrop-blur-[4px] transition-colors hover:bg-black/40 active:bg-black/80 sm:hidden"
                onClick={handleNext}
                style={{ right: `calc(50% - ${arrowOffset}px)` }}
                type="button"
              >
                <AngleRight className="size-6" />
              </button>
            </>
          )}
        </div>
      </div>

      <div className="mt-10 flex items-center justify-center gap-8 py-4">
        {socialLinks?.map(({ icon: Icon, href, label }) => (
          <a
            key={label}
            className="flex size-8 items-center justify-center text-white"
            href={href}
            rel="noopener noreferrer"
            target="_blank"
            title={label}
          >
            <Icon className="size-8" />
          </a>
        ))}
      </div>
    </SectionShell>
  );
}
